/**
 * Toast queue — top-right stack, 4s auto-dismiss, semantic color.
 *
 * Wrap your app in <ToastProvider>; then call `useToast()` anywhere:
 *
 *   const toast = useToast()
 *   toast.success('Saved.')
 *   toast.error('Server said no.')
 *   toast.info('Synced.')
 *   toast.warning('Stale data — refresh recommended.')
 *
 * Click X to dismiss, or it auto-dismisses after 4s. Stacks vertically;
 * newer toasts push older ones down.
 */
import {
  type ReactNode, createContext, useCallback, useContext, useEffect,
  useMemo, useRef, useState,
} from 'react'
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react'

export type ToastKind = 'success' | 'error' | 'info' | 'warning'

export interface ToastItem {
  id: number
  kind: ToastKind
  message: string
  /** Optional ms override (default 4000). 0 = sticky. */
  duration?: number
}

interface ToastApi {
  success: (message: string, opts?: { duration?: number }) => void
  error:   (message: string, opts?: { duration?: number }) => void
  info:    (message: string, opts?: { duration?: number }) => void
  warning: (message: string, opts?: { duration?: number }) => void
  /** Manually dismiss by id (rarely needed; auto-dismiss handles most cases). */
  dismiss: (id: number) => void
}

const ToastCtx = createContext<ToastApi | null>(null)

// v2 semantic tokens — auto-themes light/dark. info uses the accent (violet)
// family to match the prior look; success/error/warning use status families.
const KIND_STYLES: Record<ToastKind, { wrap: string; icon: string; Icon: typeof CheckCircle2 }> = {
  success: {
    wrap: 'border-success-edge bg-success-soft text-success-soft-fg',
    icon: 'text-success',
    Icon: CheckCircle2,
  },
  error: {
    wrap: 'border-danger-edge bg-danger-soft text-danger-soft-fg',
    icon: 'text-danger',
    Icon: XCircle,
  },
  info: {
    wrap: 'border-accent-edge bg-accent-soft text-accent-soft-fg',
    icon: 'text-accent',
    Icon: Info,
  },
  warning: {
    wrap: 'border-warning-edge bg-warning-soft text-warning-soft-fg',
    icon: 'text-warning',
    Icon: AlertTriangle,
  },
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])
  const nextId = useRef(1)

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const push = useCallback((kind: ToastKind, message: string, duration?: number) => {
    const id = nextId.current++
    setItems((prev) => [...prev, { id, kind, message, duration }])
  }, [])

  // Each toast schedules its own auto-dismiss after mount. We do this in
  // a child effect so the timer is per-toast (newer toasts don't extend
  // older ones).
  const api = useMemo<ToastApi>(() => ({
    success: (m, o) => push('success', m, o?.duration),
    error:   (m, o) => push('error',   m, o?.duration ?? 6000), // errors stay a bit longer
    info:    (m, o) => push('info',    m, o?.duration),
    warning: (m, o) => push('warning', m, o?.duration ?? 6000),
    dismiss,
  }), [push, dismiss])

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div
        className="fixed top-3 right-3 z-[60] flex flex-col gap-2 pointer-events-none"
        aria-live="polite"
        aria-label="Notifications"
      >
        {items.map((t) => (
          <ToastItemView key={t.id} item={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastCtx.Provider>
  )
}

function ToastItemView({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  const styles = KIND_STYLES[item.kind]
  useEffect(() => {
    const ms = item.duration ?? 4000
    if (ms <= 0) return
    const t = setTimeout(onDismiss, ms)
    return () => clearTimeout(t)
  }, [item.duration, onDismiss])

  return (
    <div
      role="status"
      className={`pointer-events-auto flex items-start gap-2 min-w-[260px] max-w-md
                  px-3 py-2 rounded-md border shadow-sm text-sm
                  animate-[fade-in_120ms_ease-out] ${styles.wrap}`}
    >
      <span className={`shrink-0 ${styles.icon} mt-0.5`} aria-hidden>
        <styles.Icon size={15} />
      </span>
      <span className="flex-1 leading-5 break-words">{item.message}</span>
      <button
        type="button"
        onClick={onDismiss}
        className="shrink-0 text-ink-faint hover:text-ink-secondary leading-none px-1 mt-0.5"
        aria-label="Dismiss"
      >
        <X size={13} />
      </button>
    </div>
  )
}

/**
 * Use `toast.success(...)` etc. inside any component below <ToastProvider>.
 * Returns a stable API object so it's safe inside useEffect deps.
 */
export function useToast(): ToastApi {
  const v = useContext(ToastCtx)
  if (!v) {
    // Dev-time fallback: log to console so missing provider doesn't crash.
    // Production should always have the provider wired in App.tsx.
    if (typeof window !== 'undefined' && !(window as any).__warnedNoToastProvider) {
      // eslint-disable-next-line no-console
      console.warn('useToast() called outside <ToastProvider>; toasts will be silent.')
      ;(window as any).__warnedNoToastProvider = true
    }
    const noop = () => {}
    return { success: noop, error: noop, info: noop, warning: noop, dismiss: noop }
  }
  return v
}
